<template>
  <div
    ref="colorRunContainer"
    v-loading="!calculationFinished"
    class="color-run-preview"
  >
    <!-- CrossOrigin needs to be before the src or else, an error pops up when drawing on canvas -->
    <!-- Similar to this : https://github.com/facebook/react/issues/14035 -->
    <!-- https://stackoverflow.com/questions/23123237/drawing-images-to-canvas-with-img-crossorigin-anonymous-doesnt-work -->
    <img
      v-for="(image, imageIndex) in colorRun"
      v-show="calculationFinished"
      :key="imageIndex"
      ref="colorRunImage"
      crossorigin="use-credentials"
      :src="image"
      :alt="getCurrentColorwayTitle(imageIndex)"
      :title="getCurrentColorwayTitle(imageIndex)"
      class="color-run-preview__image"
      @click="onClick(imageIndex)"
    >
  </div>
</template>

<script>
import ResizeObserver from 'resize-observer-polyfill'

export default {
  name: 'ColorRunPreview',

  props: {
    images: {
      type: Array,
      default: () => [],
      required: true
    }
  },

  data: () => ({
    calculationFinished: false,
    singleImageDimension: null,
    resizeObserver: null
  }),

  computed: {
    /**
     * @returns {Array}
     */
    colorRun () {
      return [...this.images].reverse()
    },

    /**
     * @returns {number}
     */
    totalImages () {
      return this.images.length
    }
  },

  watch: {
    /**
     * @param {object} newValue
     * @param {object} oldValue
     */
    colorRun (newValue, oldValue) {
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        this.singleImageDimension = null
        this.setImagesPosition()
      }
    }
  },

  mounted () {
    this.resizeObserver = new ResizeObserver((entries, observer) => {
      this.setImagesPosition()
    })
    this.resizeObserver.observe(this.$refs.colorRunContainer)
  },

  destroyed () {
    this.resizeObserver.disconnect()
  },

  methods: {
    /**
     */
    async setImagesPosition () {
      if (this.colorRun.length === 0) {
        this.calculationFinished = true

        return
      }

      this.calculationFinished = false
      const containerData = this.$refs.colorRunContainer.getBoundingClientRect()
      const containerWidth = containerData.width
      const containerHeight = containerData.height

      // this.totalImages - 1 to get the first image of the array since we're drawing images in reverse
      if (!this.singleImageDimension) {
        this.singleImageDimension = await this.getMaxImageSize(this.colorRun)
      }

      const imageWidth = this.singleImageDimension.width
      const imageHeight = this.singleImageDimension.height
      const halfTotalImages = Math.round(this.totalImages / 2)

      const params = this.getImagesParameters(
        containerWidth,
        containerHeight,
        imageWidth,
        imageHeight,
        this.totalImages,
        halfTotalImages
      )

      await this.$nextTick()

      this.$refs.colorRunImage.forEach((image, index) => {
        if (params.splitInTwoRows) {
          image.style.transform = 'none'

          if (index < halfTotalImages) {
            const topHalfContainerHeight = (containerHeight * 1) / 4
            image.style.top = `${topHalfContainerHeight -
              params.imageHeight / 2}px`
            image.style.right = `${params.offsetStart +
              index * params.offsetPerImage}px`
          } else {
            const bottomHalfContainerHeight = (containerHeight * 3) / 4
            image.style.top = `${bottomHalfContainerHeight -
              params.imageHeight / 2}px`
            image.style.right = `${params.offsetStartRow2 +
              (index - halfTotalImages) * params.offsetPerImage}px`
          }
        } else {
          image.style.right = `${params.offsetStart +
            index * params.offsetPerImage}px`
          image.style.top = null
          image.style.transform = 'translateY(-50%)'
        }

        image.style.height = `${params.imageHeight}px`
      })

      this.calculationFinished = true
    },

    /**
     * @param   {number} containerWidth
     * @param   {number} containerHeight
     * @param   {number} imageWidth
     * @param   {number} imageHeight
     * @param   {number} totalImages
     * @param   {number} halfTotalImages
     *
     * @returns {object}
     */
    getImagesParameters (
      containerWidth,
      containerHeight,
      imageWidth,
      imageHeight,
      totalImages,
      halfTotalImages
    ) {
      const ratio = imageWidth / imageHeight

      if (imageHeight > containerHeight) {
        imageWidth = imageWidth * (containerHeight / imageHeight)
      }

      const offsetPercentage = 0.5
      let offsetPerImage = imageWidth * offsetPercentage
      const totalWidth = imageWidth + offsetPerImage * (totalImages - 1)

      if (totalWidth > containerWidth) {
        // Equation
        // imagewidth = containerWidth / (100% [of the image] + 50%[of the image] * total images - the full image's offset)
        imageWidth = containerWidth / (1 + offsetPercentage * (totalImages - 1))
        offsetPerImage = imageWidth * offsetPercentage
      }

      // Split in 2 rows if bigger than one row
      imageHeight = imageWidth / ratio
      let splitInTwoRows = false

      const containerHalfHeightWithoutMargins = (containerHeight / 2) * 0.9

      if (imageHeight < containerHalfHeightWithoutMargins && totalImages > 1) {
        splitInTwoRows = true
        imageWidth =
          containerWidth /
          (1 + offsetPercentage * (totalImages - halfTotalImages))
        offsetPerImage = imageWidth * offsetPercentage

        imageHeight = imageWidth / ratio

        if (imageHeight > containerHalfHeightWithoutMargins) {
          imageWidth = containerHalfHeightWithoutMargins * ratio
          offsetPerImage = imageWidth * offsetPercentage
        }
      }

      if (totalImages === 1) {
        offsetPerImage = 0
      }

      let offsetStart = null
      let offsetStartRow2 = null

      if (splitInTwoRows) {
        offsetStart =
          containerWidth / 2 -
          imageWidth / 2 -
          offsetPerImage * ((halfTotalImages - 1) / 2)
        offsetStartRow2 =
          containerWidth / 2 -
          imageWidth / 2 -
          offsetPerImage * ((totalImages - halfTotalImages - 1) / 2)
      } else {
        offsetStart =
          containerWidth / 2 -
          imageWidth / 2 -
          offsetPerImage * ((totalImages - 1) / 2)
      }

      return {
        splitInTwoRows,
        offsetStart,
        offsetStartRow2,
        offsetPerImage,
        imageHeight: imageWidth / ratio
      }
    },

    /**
     * @param   {string[]} urlArray
     *
     * @returns {object}
     */
    async getMaxImageSize (urlArray) {
      const promises = urlArray.map(url => this.getImageSize(url))
      const sizes = await Promise.all(promises)

      return sizes.reduce((accumulator, size) => {
        const oldRatio = accumulator.width
          ? accumulator.width / accumulator.height
          : null
        const newRatio = size.width / size.height

        if (oldRatio && oldRatio > newRatio) {
          return accumulator
        } else {
          return size
        }
      }, {})
    },

    /**
     * @param   {string}  url
     *
     * @returns {Promise}
     */
    getImageSize (url) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'use-credentials'
        /**
         */
        img.onload = function () {
          resolve({
            width: this.width,
            height: this.height
          })
        }

        img.src = url
      })
    },

    /**
     * @param   {number} imageIndex
     *
     * @returns {number}
     */
    getCurrentColorwayTitle (imageIndex) {
      return `color run: ${this.totalImages - imageIndex}`
    },

    /**
     * @param {number} colorWayIndex
     */
    onClick (colorWayIndex) {
      const originalIndex = this.totalImages - 1 - colorWayIndex
      this.$emit('click-colorway', originalIndex)
    }
  }
}
</script>

<style lang="scss" scoped>
.color-run-preview {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.color-run-preview__image {
  position: absolute;
  top: $position-center;
  max-width: 100%;
  max-height: 100%;
  transform: translateY(-50%);
}
</style>
